import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Card,
  Row,
  Col,
  Tag,
  Button,
  Modal,
  Space,
  Typography,
  Empty,
  Spin,
  message,
  Image,
  Divider,
  Badge,
} from 'antd';
import {
  CheckOutlined,
  ArrowLeftOutlined,
  UserOutlined,
} from '@ant-design/icons';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import api from '../../utils/api';

const { Title, Text } = Typography;

const StyledCard = styled(Card)`
  border-radius: 16px !important;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
  margin-bottom: 24px;
`;

const SuggestionCard = styled(Card)`
  border-radius: 12px !important;
  margin-bottom: 16px;
  transition: all 0.3s ease;
  border: 2px solid ${props => {
    if (props.$confidence === 'high') return '#52c41a';
    if (props.$confidence === 'medium') return '#faad14';
    return '#ff4d4f';
  }};

  &:hover {
    transform: translateY(-4px);
    box-shadow: 0 8px 16px rgba(0, 0, 0, 0.12);
  }
`;

const ImageGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(120px, 1fr));
  gap: 12px;
  margin-top: 16px;

  .ant-image {
    border-radius: 8px;
    overflow: hidden;
  }
`;

const MatchUnknownFace = () => {
  const { unknown_id } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [suggestions, setSuggestions] = useState([]);
  const [unknownPhotos, setUnknownPhotos] = useState([]);
  const [merging, setMerging] = useState(false);

  useEffect(() => {
    loadSuggestions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [unknown_id]);

  const loadSuggestions = async () => {
    setLoading(true);
    try {
      const data = await api.getSuggestedMatches(unknown_id, { top_n: 5 });

      // Pre-compute photo URLs for suggestions
      const suggestionsWithUrls = (data.suggestions || []).map(suggestion => ({
        ...suggestion,
        top_matching_images: suggestion.top_matching_images.map(img => ({
          ...img,
          url: api.getEmployeePhoto(suggestion.emp_id, img.image_filename)
        }))
      }));

      setSuggestions(suggestionsWithUrls);

      // Pre-compute unknown photo URLs
      const unknownPhotosWithUrls = (data.unknown_photos || []).map(photo => ({
        ...photo,
        url: api.getUnknownPhoto(unknown_id, photo.index)
      }));
      setUnknownPhotos(unknownPhotosWithUrls);

      if (data.suggestions && data.suggestions.length === 0) {
        message.warning('No matching employees found. This face may not have a valid embedding or quality may be too low.');
      }
    } catch (error) {
      if (error.message && error.message.includes('not found in embeddings')) {
        message.error('This unknown face does not have a valid embedding. The image quality may be too low.');
      } else {
        message.error('Failed to load match suggestions: ' + (error.message || 'Unknown error'));
      }
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleConfirmMatch = async (suggestion) => {
    Modal.confirm({
      title: 'Confirm Match',
      icon: <CheckOutlined style={{ color: '#52c41a' }} />,
      content: (
        <div>
          <p>Are you sure this unknown face belongs to:</p>
          <p><strong>{suggestion.emp_id} - {suggestion.full_name}</strong></p>
          <p style={{ color: '#666', fontSize: 12 }}>
            All {unknownPhotos.length} images will be merged into this employee's profile
            and the unknown face record will be deleted.
          </p>
        </div>
      ),
      okText: 'Yes, Merge',
      okType: 'primary',
      cancelText: 'Cancel',
      onOk: async () => {
        setMerging(true);
        try {
          const result = await api.mergeUnknownToEmployee(unknown_id, suggestion.emp_id);
          message.success(`Successfully merged into ${result.full_name}! Added ${result.images_copied} images.`);
          navigate('/unknown-faces');
        } catch (error) {
          message.error(error.message || 'Failed to merge');
        } finally {
          setMerging(false);
        }
      }
    });
  };

  const getConfidenceBadge = (confidence, score) => {
    const config = {
      high: { color: 'success', text: 'HIGH CONFIDENCE' },
      medium: { color: 'warning', text: 'MEDIUM CONFIDENCE' },
      low: { color: 'error', text: 'LOW CONFIDENCE' }
    };
    const { color, text } = config[confidence] || config.low;
    return (
      <Badge
        count={text}
        style={{
          backgroundColor: color === 'success' ? '#52c41a' : color === 'warning' ? '#faad14' : '#ff4d4f',
        }}
      />
    );
  };

  return (
    <div>
      <Space style={{ marginBottom: 24 }}>
        <Button
          icon={<ArrowLeftOutlined />}
          onClick={() => navigate('/unknown-faces')}
        >
          Back to Unknown Faces
        </Button>
      </Space>

      <Title level={2}>Match Unknown Face: {unknown_id}</Title>

      <Spin spinning={loading || merging} tip={merging ? 'Merging...' : 'Loading...'}>
        {/* Unknown Face Photos */}
        <StyledCard
          title={
            <Space>
              <UserOutlined />
              <Text strong>Unknown Face Photos ({unknownPhotos.length})</Text>
            </Space>
          }
        >
          {unknownPhotos.length > 0 ? (
            <ImageGrid>
              {unknownPhotos.map((photo) => (
                <Image
                  key={photo.index}
                  src={photo.url}
                  alt={`${unknown_id}-${photo.index}`}
                  style={{
                    width: '100%',
                    height: 120,
                    objectFit: 'cover',
                  }}
                  fallback="https://via.placeholder.com/120x120?text=Error"
                />
              ))}
            </ImageGrid>
          ) : (
            <Empty description="No photos found" />
          )}
        </StyledCard>

        <Divider />

        {/* Suggestions */}
        <Title level={4}>Possible Matches (Top 5)</Title>

        {suggestions.length > 0 ? (
          <Row gutter={[16, 16]}>
            {suggestions.map((suggestion, index) => (
              <Col span={24} key={suggestion.emp_id}>
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.1 }}
                >
                  <SuggestionCard
                    $confidence={suggestion.confidence}
                    title={
                      <Space style={{ width: '100%', justifyContent: 'space-between' }}>
                        <Space>
                          <Tag color="blue" style={{ fontSize: 16, padding: '4px 12px' }}>
                            #{index + 1}
                          </Tag>
                          <Text strong style={{ fontSize: 16 }}>
                            {suggestion.emp_id} - {suggestion.full_name}
                          </Text>
                          {suggestion.department && (
                            <Tag>{suggestion.department}</Tag>
                          )}
                        </Space>
                        <Space>
                          {getConfidenceBadge(suggestion.confidence, suggestion.match_score)}
                          <Tag color="purple" style={{ fontSize: 14 }}>
                            Match: {(suggestion.match_score * 100).toFixed(1)}%
                          </Tag>
                        </Space>
                      </Space>
                    }
                  >
                    <Row gutter={16}>
                      <Col span={16}>
                        <Text strong>Top Matching Images:</Text>
                        <ImageGrid>
                          {suggestion.top_matching_images.map((img, idx) => (
                            <div key={idx} style={{ position: 'relative' }}>
                              <Image
                                src={img.url}
                                alt={`${suggestion.emp_id}-${idx}`}
                                style={{
                                  width: '100%',
                                  height: 120,
                                  objectFit: 'cover',
                                }}
                                fallback="https://via.placeholder.com/120x120?text=Error"
                              />
                              <Tag
                                color="blue"
                                style={{
                                  position: 'absolute',
                                  bottom: 4,
                                  right: 4,
                                  margin: 0,
                                }}
                              >
                                {(img.similarity * 100).toFixed(0)}%
                              </Tag>
                            </div>
                          ))}
                        </ImageGrid>
                      </Col>

                      <Col span={8}>
                        <Space direction="vertical" style={{ width: '100%' }} size="large">
                          <div>
                            <Text type="secondary">Employee ID:</Text>
                            <br />
                            <Text strong>{suggestion.emp_id}</Text>
                          </div>
                          {suggestion.email && (
                            <div>
                              <Text type="secondary">Email:</Text>
                              <br />
                              <Text>{suggestion.email}</Text>
                            </div>
                          )}
                          {suggestion.team && (
                            <div>
                              <Text type="secondary">Team:</Text>
                              <br />
                              <Text>{suggestion.team}</Text>
                            </div>
                          )}
                          <Divider style={{ margin: '8px 0' }} />
                          <Button
                            type="primary"
                            icon={<CheckOutlined />}
                            size="large"
                            block
                            onClick={() => handleConfirmMatch(suggestion)}
                          >
                            Confirm Match
                          </Button>
                        </Space>
                      </Col>
                    </Row>
                  </SuggestionCard>
                </motion.div>
              </Col>
            ))}
          </Row>
        ) : (
          !loading && (
            <Empty
              description="No matching employees found. This face may be genuinely unknown."
              style={{ padding: '60px 0' }}
            />
          )
        )}
      </Spin>
    </div>
  );
};

export default MatchUnknownFace;
